import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserInfo } from '@auth/models/auth.state';
import { takeUntil, tap } from 'rxjs/operators';
import { AuthState } from '@auth/store/auth/auth.state';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { TranslateService } from '@ngx-translate/core';
import { Locale } from '@shared/models/locale';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import {
  AddNotificationCatalog,
  DeleteNotificationCatalog,
  GetNotificationCatalogs
} from '../../../store/catalog-change-notifications/catalog-change-notifications.action';
import { AddFavoriteCatalog, DeleteFavoriteCatalog } from '../../../store/favorite-catalogs/favorite-catalogs.action';
import { CatalogChangeNotificationsState } from '../../../store/catalog-change-notifications/catalog-change-notifications.state';
import { FavoriteCatalogsState } from '../../../store/favorite-catalogs/favorite-catalogs.state';
import { ChangeCatalogFilter } from '../../../modules/catalog-data/store/catalog-table.actions';
import { CatalogTableState } from '../../../modules/catalog-data/store/catalog-table.state';
import { CatalogFilter } from '../../../modules/catalog-data/models/catalog.service';

@Component({
  selector: 'app-favorites-and-subscribe-catalog',
  templateUrl: './favorites-and-subscribe-catalog.component.html',
  styleUrls: ['./favorites-and-subscribe-catalog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class FavoritesAndSubscribeCatalogComponent implements OnInit {
  private _currentUser: UserInfo | null;
  private _favoriteCatalogIds: number[] | null;
  private _subscribeCatalogIds: number[] | null;
  private _catalogId: number;

  public subscribeCatalogTitle = '';
  public subscribeCatalogIcon: CustomIcons;
  public langChange$: Observable<LangChangeEvent>;
  public componentDestroy: () => Subject<any>;
  public favoriteCatalogTitle = '';
  public isRuLocale = true;
  public favoriteCatalogIcon: CustomIcons;
  @Input() public isDynamic: boolean;
  @Input() public disable = false;
  @Select(AuthState.authenticatedUser) public currentUser$: Observable<UserInfo>;
  @Input() public set catalogId(value: string) {
    if (value) {
      this._catalogId = +value;
      this.changeSubscribeCatalogTitle();
      this.changeFavoriteCatalogTitle();
    }
  }

  @Select(CatalogChangeNotificationsState.notificationCatalogIds) public subscribeCatalogIds$: Observable<number[]>;
  @Select(FavoriteCatalogsState.favoriteCatalogIds) public favoriteCatalogIds$: Observable<number[]>;

  constructor(private translateService: TranslateService, private store: Store, public dialog: MatDialog, private cdr: ChangeDetectorRef) {}

  private popCatalogIdFromFilterAction(): ChangeCatalogFilter {
    const catalogAfterPop =
      this.store.selectSnapshot(CatalogTableState.getCatalogFilter)?.catalogIds?.filter(catalogId => catalogId !== this._catalogId) ?? [];

    const filterForChange: CatalogFilter = { catalogIds: catalogAfterPop };

    if (catalogAfterPop.length === 0) filterForChange.newRecommendedFavorites = 'all';

    return new ChangeCatalogFilter(filterForChange, true);
  }

  private changeSubscribeCatalogTitle(): void {
    this.subscribeCatalogTitle = 'DATA.SHARED.MORE_VERT_MENU.SUBSCRIBE_FOR_CHANGES';
    this.subscribeCatalogIcon = CustomIcons.Notifications;
    if (!this._currentUser) {
      return;
    }
    const isSubscribe = !!this._subscribeCatalogIds?.find(catalogId => catalogId === this._catalogId);
    if (this._currentUser) {
      if (isSubscribe) {
        this.subscribeCatalogTitle = 'DATA.SHARED.MORE_VERT_MENU.UNSUBSCRIBE_FROM_CHANGES';
        this.subscribeCatalogIcon = CustomIcons.FillNotifications;
      }
    }
    this.cdr.detectChanges();
  }

  private changeFavoriteCatalogTitle(): void {
    this.favoriteCatalogTitle = 'DATA.SHARED.MORE_VERT_MENU.ADD_TO_FAVORITES';
    this.favoriteCatalogIcon = CustomIcons.Bookmark;
    if (!this._currentUser) {
      return;
    }
    const isFavorite = !!this._favoriteCatalogIds?.find(catalogId => catalogId === this._catalogId);
    if (this._currentUser) {
      if (isFavorite) {
        this.favoriteCatalogTitle = 'DATA.SHARED.MORE_VERT_MENU.DELETE_FROM_FAVORITES';
        this.favoriteCatalogIcon = CustomIcons.BookmarkFill;
      }
    }
    this.cdr.detectChanges();
  }

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
    } else {
      this.isRuLocale = false;
    }
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
    this.subscribeCatalogIds$.pipe(takeUntil(this.componentDestroy())).subscribe(res => {
      this._subscribeCatalogIds = res;
      this.changeSubscribeCatalogTitle();
    });

    this.favoriteCatalogIds$.pipe(takeUntil(this.componentDestroy())).subscribe(res => {
      this._favoriteCatalogIds = res;
      this.changeFavoriteCatalogTitle();
    });

    this.currentUser$.pipe(takeUntil(this.componentDestroy())).subscribe(res => {
      if (res) {
        this.store.dispatch(new GetNotificationCatalogs());
      }
      this._currentUser = res;
      this.changeSubscribeCatalogTitle();
      this.changeFavoriteCatalogTitle();
    });
  }

  public subscribeCatalog(): void {
    if (!this._currentUser) {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'AddNotification';
      authorizationDialog.componentInstance.operationValue = this._catalogId;
    }

    const isSubscribe = !!this._subscribeCatalogIds?.find(catalogId => catalogId === this._catalogId);

    if (this._currentUser && this._catalogId) {
      this.store.dispatch(
        isSubscribe
          ? [new DeleteNotificationCatalog(this._catalogId), this.popCatalogIdFromFilterAction()]
          : new AddNotificationCatalog(this._catalogId)
      );
    }
  }

  public addFavoriteCatalog(): void {
    if (!this._currentUser) {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'AddFavoriteItem';
      authorizationDialog.componentInstance.operationValue = this._catalogId;
    }

    const isFavorite = !!this._favoriteCatalogIds?.find(catalogId => catalogId === this._catalogId);
    if (this._currentUser && this._catalogId) {
      this.store.dispatch(
        isFavorite ? [new DeleteFavoriteCatalog(this._catalogId), this.popCatalogIdFromFilterAction()] : new AddFavoriteCatalog(this._catalogId)
      );
    }
  }
}
